import { consola } from 'consola'
/**
 * Composable для работы с настройками модального окна правил доставки
 * Использует Capacitor SecureStorage для нативных приложений
 * Fallback на localStorage для веб-версии
 */
export function useDeliveryRulesStorage() {
  
  /**
   * Сохраняет значение в storage
   */
  const setItem = async (key: string, value: string): Promise<void> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.set({ key, value })
        } else {
          // Fallback на localStorage для веб
          localStorage.setItem(key, value)
        }
      }
    } catch (error) {
      consola.warn(`[DeliveryRulesStorage] Failed to save ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        localStorage.setItem(key, value)
      }
    }
  }

  /**
   * Получает значение из storage
   */
  const getItem = async (key: string): Promise<string | null> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          const result = await SecureStoragePlugin.get({ key })
          return result?.value || null
        } else {
          // Fallback на localStorage для веб
          return localStorage.getItem(key)
        }
      }
      return null
    } catch (error) {
      consola.warn(`[DeliveryRulesStorage] Failed to get ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        return localStorage.getItem(key)
      }
      return null
    }
  }

  /**
   * Удаляет значение из storage
   */
  const removeItem = async (key: string): Promise<void> => {
    try {
      if (process.client) {
        const { Capacitor } = await import('@capacitor/core')
        
        if (Capacitor.isNativePlatform()) {
          // Используем SecureStorage для нативных приложений
          const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
          await SecureStoragePlugin.remove({ key })
        } else {
          // Fallback на localStorage для веб
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      consola.warn(`[DeliveryRulesStorage] Failed to remove ${key}:`, error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        localStorage.removeItem(key)
      }
    }
  }

  // Константы для ключей
  const KEYS = {
    DELIVERY_RULES_HIDDEN: 'deliveryRulesModalHidden',
  } as const

  /**
   * Специфичные методы для настроек модального окна доставки
   */
  const setDeliveryRulesHidden = async (hidden: boolean): Promise<void> => {
    await setItem(KEYS.DELIVERY_RULES_HIDDEN, hidden.toString())
  }

  const isDeliveryRulesHidden = async (): Promise<boolean> => {
    const value = await getItem(KEYS.DELIVERY_RULES_HIDDEN)
    const result = value === 'true'
    consola.debug('📦 [DeliveryRulesStorage] isDeliveryRulesHidden - value:', value, 'result:', result)
    return result
  }

  /**
   * Сбрасывает настройку (показывать модальное окно снова)
   */
  const resetDeliveryRulesSetting = async (): Promise<void> => {
    await removeItem(KEYS.DELIVERY_RULES_HIDDEN)
  }

  return {
    // Общие методы
    setItem,
    getItem,
    removeItem,
    
    // Специфичные для модального окна доставки
    setDeliveryRulesHidden,
    isDeliveryRulesHidden,
    resetDeliveryRulesSetting,
    
    // Константы
    KEYS,
  }
}
